import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import moment from "moment";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import Alert from "../Alert";
import PermissionGate from "../layout/PermissionGate";
import Pagination from "../layout/Pagination";
import normalize from "../utils/NormalizeSelect";

const ALL_STATUS_OPTION = { value: "all", label: "All Statuses" };

const DriverBusyReport = () => {
    const [data, setData] = useState([]);
    const [statusTypes, setStatusTypes] = useState([]);
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState(ALL_STATUS_OPTION);
    const [isLoading, setIsLoading] = useState(true);
    const [start, setStart] = useState(
        moment().subtract(1, "months").startOf("day").format("YYYY-MM-DD"),
    );
    const [end, setEnd] = useState(moment().endOf("day").format("YYYY-MM-DD"));
    const dateRangePickerRef = useRef(null);
    const token = localStorage.getItem("token");
    const limit = process.env.REACT_APP_PAGINATION_LIMIT;

    const fetchStatusTypes = () => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}driver-busy-status-types`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((res) => {
                if (res.data.code === 1) {
                    setStatusTypes(res.data.data ?? []);
                }
            })
            .catch(console.error);
    };

    const fetchData = () => {
        setIsLoading(true);
        axios
            .get(
                `${process.env.REACT_APP_API_ENDPOINT}driver-busy-report?startDate=${start}&endDate=${end}&page=${page}&limit=${limit}&search=${search}&status=${status?.value ?? "all"}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    if (page > 1 && res.data.data.length === 0) {
                        setPage(page - 1);
                    } else {
                        setData(res.data.data);
                        setCount(res.data.count);
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            })
            .catch(console.error);
    };

    const exportData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}driver-busy-report-export?startDate=${start}&endDate=${end}&search=${search}&status=${status?.value ?? "all"}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const timestamp = Date.now();
            const filename = `driver_busy_report_${timestamp}.xlsx`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const openCalendar = () => {
        if (dateRangePickerRef.current) {
            dateRangePickerRef.current.flatpickr.open();
        }
    };

    const reset = () => {
        setPage(1);
        setSearch("");
        setStatus(ALL_STATUS_OPTION);
        const defaultStart = moment()
            .subtract(1, "months")
            .startOf("day")
            .format("YYYY-MM-DD");
        const defaultEnd = moment().endOf("day").format("YYYY-MM-DD");
        setStart(defaultStart);
        setEnd(defaultEnd);
    };

    useEffect(() => {
        fetchStatusTypes();
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [start, end, page, search, status]);

    return (
        <div className="d-flex flex-column flex-root">
            <Alert />
            <div className="page d-flex flex-row flex-column-fluid">
                <div
                    className="wrapper d-flex flex-column flex-row-fluid"
                    id="kt_wrapper"
                >
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        Driver Busy Report
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start  container-xxl "
                        >
                            <div className="content flex-row-fluid" id="kt_content">
                                <PermissionGate required={`view_driver_busy_report`}>
                                    <div className="card">
                                        <div className="p-6">
                                            <div className="d-block d-md-flex justify-content-between gap-2">
                                                <div className="d-block d-md-flex gap-2">
                                                    <div className="mb-3">
                                                        <input
                                                            value={search}
                                                            onChange={(e) => {
                                                                setPage(1);
                                                                setSearch(e.target.value);
                                                            }}
                                                            type="text"
                                                            className="form-control w-100"
                                                            placeholder="Search Admin User"
                                                        />
                                                    </div>
                                                    <div className="mb-3" style={{ minWidth: "220px" }}>
                                                        <Select
                                                            className="react-select-styled react-select-bordered"
                                                            classNamePrefix="react-select"
                                                            value={status}
                                                            onChange={(e) => {
                                                                setPage(1);
                                                                setStatus(e ?? ALL_STATUS_OPTION);
                                                            }}
                                                            options={[
                                                                ALL_STATUS_OPTION,
                                                                ...statusTypes.map((type) => ({
                                                                    value: type.key,
                                                                    label: type.label,
                                                                })),
                                                            ]}
                                                            filterOption={(option, input) => {
                                                                return normalize(option.label).includes(
                                                                    normalize(input),
                                                                );
                                                            }}
                                                            placeholder="Status"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-block d-md-flex gap-2 flex-shrink-0">
                                                    <div className="d-flex position-relative flex-shrink-0 mb-3">
                                                        <Flatpickr
                                                            ref={dateRangePickerRef}
                                                            value={[new Date(start), new Date(end)]}
                                                            options={{ dateFormat: "d-m-Y", mode: "range" }}
                                                            onChange={([start, end]) => {
                                                                if (start && end) {
                                                                    setPage(1);
                                                                    setStart(moment(start).format("YYYY-MM-DD"));
                                                                    setEnd(moment(end).format("YYYY-MM-DD"));
                                                                }
                                                            }}
                                                            className="form-control form-control-lg"
                                                            placeholder="Pick date"
                                                        />
                                                        <i
                                                            onClick={openCalendar}
                                                            className="bi-duotone bi-calendar fs-5 position-absolute"
                                                            style={{
                                                                top: "28%",
                                                                right: "5%",
                                                                cursor: "pointer",
                                                            }}
                                                        ></i>
                                                    </div>
                                                    <div className="w-100 w-md-auto mb-3">
                                                        <button
                                                            onClick={reset}
                                                            className="btn btn-danger w-100"
                                                        >
                                                            <i className="ki-duotone ki-wrench fs-1">
                                                                <span className="path1"></span>
                                                                <span className="path2"></span>
                                                            </i>
                                                            Reset
                                                        </button>
                                                    </div>
                                                    <PermissionGate
                                                        required={`export_driver_busy_report`}
                                                        fallback={<></>}
                                                        show={false}
                                                    >
                                                        <div className="w-100 w-md-auto">
                                                            <button
                                                                onClick={exportData}
                                                                className="btn btn-primary w-100"
                                                            >
                                                                <i className="ki-duotone ki-cloud-download fs-1">
                                                                    <span className="path1"></span>
                                                                    <span className="path2"></span>
                                                                </i>
                                                                Export
                                                            </button>
                                                        </div>
                                                    </PermissionGate>
                                                </div>
                                            </div>
                                        </div>

                                        {isLoading === true ? (
                                            <Loader />
                                        ) : (
                                            <div className="card-body py-4 text-center">
                                                {data?.length === 0 ? (
                                                    <div className="text-center w-100">
                                                        <img
                                                            className="w-25 mx-auto py-20"
                                                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                                            alt="no-content"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table
                                                            className="table align-middle table-row-dashed fs-6 gy-5"
                                                            id="kt_table_driver_busy_report"
                                                        >
                                                            <thead>
                                                                <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                                                    <th className="text-center min-w-150px">
                                                                        Date &amp; Time
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Admin User
                                                                    </th>
                                                                    <th className="text-center min-w-150px">
                                                                        Driver Name
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Driver Phone
                                                                    </th>
                                                                    <th className="text-center min-w-120px">
                                                                        Status
                                                                    </th>
                                                                    <th className="text-center min-w-200px">
                                                                        Details
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="text-gray-600 fw-semibold">
                                                                {data &&
                                                                    data.map((log, index) => {
                                                                        const statusLabel =
                                                                            statusTypes.find(
                                                                                (type) => type.key === log?.status,
                                                                            )?.label ?? log?.status;
                                                                        return (
                                                                            <tr className="text-center" key={log?.driverLogId ?? index}>
                                                                                <td>
                                                                                    {log?.createdAt
                                                                                        ? moment(log.createdAt).format(
                                                                                            "DD-MM-YYYY hh:mm A",
                                                                                        )
                                                                                        : "---"}
                                                                                </td>
                                                                                <td>{log?.adminUsername ?? "---"}</td>
                                                                                <td>{log?.driverName ?? "---"}</td>
                                                                                <td>{log?.driverPhone ?? "---"}</td>
                                                                                <td>{statusLabel ?? "---"}</td>
                                                                                <td>{log?.details ?? "---"}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {count && Math.ceil(count / limit) > 1 ? (
                                                    <Pagination
                                                        page={page}
                                                        setPage={setPage}
                                                        count={count}
                                                        limit={limit}
                                                    />
                                                ) : (
                                                    ""
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </PermissionGate>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default DriverBusyReport;